import { v4 } from 'uuid';

import { ImportDialogUsedFrom, ImportSource } from 'interfaces/import';
import { AnalyticsEventKey, logAnalyticsEvent } from 'modules/analytics/logAnalyticsEvent';
import { getSDK } from 'modules/sdk/lib/sdk';

import { useLibraryStore } from '../libraryStore';
import { useImportStore } from './';

export const registerCancelImportFunction = (id: string, func: () => void) => {
  useImportStore.setState(state => ({
    cancelImportFunctions: { ...state.cancelImportFunctions, [id]: func }
  }));
};

export const openImportDialog = (payload?: { source?: ImportSource | null; showBackButton?: boolean }) => {
  useImportStore.setState({
    isModalOpen: true,
    source: payload?.source ?? null,
    showBackButton: payload?.showBackButton ?? true
  });
};

export const closeImportDialog = () => {
  useImportStore.setState({
    isModalOpen: false,
    source: null,
    showBackButton: true
  });
};

export const setImportSource = (payload: { source: ImportSource | null; dialogUsedFrom: ImportDialogUsedFrom | null }) => {
  useImportStore.setState({
    source: payload.source,
    dialogUsedFrom: payload.dialogUsedFrom
  });

  if (payload.source && payload.dialogUsedFrom) {
    logAnalyticsEvent(AnalyticsEventKey.libraryImportDialogSourceOpened, {
      source: payload.source,
      dialogUsedFrom: payload.dialogUsedFrom
    });
  }
};

export const importFile = (payload: { id: string; file: File }) => {
  useImportStore.setState(state => ({
    files: { ...state.files, [payload.id]: { state: 'in-progress', file: payload.file } }
  }));
};

export const setImportFileCompleted = (payload: { id: string; itemId: string }) => {
  useLibraryStore.setState(state => ({
    userRecordItemsCount: state.userRecordItemsCount + 1
  }));

  useImportStore.setState(state => {
    const { cancelImportFunctions } = state;
    const fileState = state.files[payload.id];
    if (!fileState) return state;

    const { [payload.id]: _, ...rest } = cancelImportFunctions;

    return {
      files: { ...state.files, [payload.id]: { ...fileState, state: 'completed', itemId: payload.itemId } },
      cancelImportFunctions: rest
    };
  });
};

export const addFailureImportFile = (file: File) => {
  const id = v4();

  useImportStore.setState(state => {
    return {
      files: { ...state.files, [id]: { file, state: 'failed' } }
    };
  });
};

export const setImportFileFailed = (id: string) => {
  useImportStore.setState(state => {
    const fileState = state.files[id];
    if (!fileState) return state;

    return {
      files: { ...state.files, [id]: { ...fileState, state: 'failed' } }
    };
  });
};

export const resetImportingFiles = () => {
  useImportStore.setState({ files: {} });
};

export const cleanImportFile = (id: string) => {
  useImportStore.setState(state => {
    const { files } = state;
    const { [id]: _, ...newFiles } = files;
    return { files: newFiles };
  });
};

export const removeImportFile = async (id: string) => {
  let isCancelled = false;
  useImportStore.setState(state => {
    const { files, cancelImportFunctions } = state;
    const { [id]: _, ...newFiles } = files;
    const { [id]: cancelImportFunction, ...newCancelImportFunctions } = cancelImportFunctions;

    if (cancelImportFunction) {
      cancelImportFunction();
      isCancelled = true;
    }

    return { files: newFiles, cancelImportFunctions: newCancelImportFunctions };
  });

  if (!isCancelled) {
    (await getSDK()).library.deleteItem(id);
  }
};

export const setUpsellModalOpen = (isOpen: boolean) => {
  useImportStore.setState({ isUpsellModalOpen: isOpen });
};

export const importStoreActions = {
  openImportDialog,
  closeImportDialog,
  setImportSource,
  importFile,
  setImportFileCompleted,
  setImportFileFailed,
  resetImportingFiles,
  registerCancelImportFunction,
  setUpsellModalOpen
};
